/**
 * Sub Banner Slider Section
 * - Swiper を動的にロード(既にヒーローで読み込まれていればスキップ)
 * - 1画面に見せる枚数は管理画面から可変(小数対応)
 * - 左右余白は CSS 側で管理画面設定を反映
 * - PC/SPブレークポイント: 750px (ヒーローと共通)
 * - 見せる枚数が偶数のときのみ左寄せ、それ以外は中央寄せ
 */
(() => {
  'use strict';

  const CONFIG = {
    BP: '(min-width: 750px)',
    SWIPER_CSS: 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css',
    SWIPER_JS: 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js',
    SPEED: 520,
    POLL: 30,
    TIMEOUT: 8000,
    DEBOUNCE: 80,
    GAP_PC: 15,
    GAP_SP: 10,
    PER_PC: 3,
    PER_SP: 2.5,
  };

  const bp = window.matchMedia(CONFIG.BP);
  let swiperPromise = null;

  /* ---------- Swiper ライブラリの遅延ロード ---------- */
  const loadSwiper = () => {
    if (window.Swiper) return Promise.resolve();
    if (swiperPromise) return swiperPromise;

    swiperPromise = new Promise((resolve, reject) => {
      if (!document.querySelector('link[data-swiper-css]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = CONFIG.SWIPER_CSS;
        link.media = 'print';
        link.onload = () => { link.media = 'all'; };
        link.dataset.swiperCss = '1';
        document.head.appendChild(link);
      }

      if (document.querySelector('script[data-swiper-js]')) {
        const poll = setInterval(() => {
          if (window.Swiper) {
            clearInterval(poll);
            resolve();
          }
        }, CONFIG.POLL);
        setTimeout(() => {
          clearInterval(poll);
          reject(new Error('Swiper load timeout'));
        }, CONFIG.TIMEOUT);
        return;
      }

      const script = document.createElement('script');
      script.src = CONFIG.SWIPER_JS;
      script.defer = true;
      script.dataset.swiperJs = '1';
      script.onload = resolve;
      script.onerror = () => reject(new Error('Swiper script error'));
      document.head.appendChild(script);
    });

    return swiperPromise;
  };

  /* ---------- セクション単位の初期化 ---------- */
  const initWrap = async (wrap) => {
    const root = wrap.querySelector('[data-sbs]');
    if (!root) return;

    const sid = root.dataset.sectionId;
    const swiperEl = document.getElementById(`sbs-swiper-${sid}`);
    const slideCount = Number(wrap.dataset.slideCount || 0);
    const showDots = root.dataset.showDots === 'true';

    const getGap = () => Number(
      bp.matches
        ? (root.dataset.gapPc || CONFIG.GAP_PC)
        : (root.dataset.gapSp || CONFIG.GAP_SP)
    );

    const getPerView = () => {
      const raw = bp.matches
        ? (wrap.dataset.perPc || CONFIG.PER_PC)
        : (wrap.dataset.perSp || CONFIG.PER_SP);
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : (bp.matches ? CONFIG.PER_PC : CONFIG.PER_SP);
    };

    if (slideCount <= 1) {
      wrap.classList.add('is-ready');
      return;
    }

    try {
      await loadSwiper();
    } catch (err) {
      console.error('[Sub Banner Slider] Swiper load failed:', err);
      wrap.classList.add('is-ready');
      return;
    }

    const prevBtn = root.querySelector('[data-sbs-prev]');
    const nextBtn = root.querySelector('[data-sbs-next]');
    const dotsWrap = root.querySelector('[data-sbs-dots]');

    let swiper = null;

    const destroy = () => {
      try {
        swiper?.destroy(true, true);
      } catch (_) { /* noop */ }
      swiper = null;
    };

    /* ---------- Swiper セットアップ ---------- */
    const setup = () => {
      const gap = getGap();
      const perView = getPerView();

      // 見せる枚数が偶数(2,4,6)のときのみ左詰め、それ以外(奇数・小数)は中央寄せ
      const isEvenInteger = Number.isInteger(perView) && perView % 2 === 0;
      const centered = !isEvenInteger;

      // ドット再生成
      if (dotsWrap && showDots) dotsWrap.innerHTML = '';
      const dots = [];
      if (showDots && dotsWrap) {
        for (let i = 0; i < slideCount; i++) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'sbs__dot';
          btn.addEventListener('click', () => swiper?.slideToLoop(i, CONFIG.SPEED));
          dotsWrap.appendChild(btn);
          dots.push(btn);
        }
      }

      swiper = new Swiper(swiperEl, {
        loop: true,
        speed: CONFIG.SPEED,
        navigation: (prevBtn && nextBtn) ? { prevEl: prevBtn, nextEl: nextBtn } : false,
        centeredSlides: centered,
        slidesPerView: perView,
        spaceBetween: gap,
        slidesPerGroup: 1,
        loopAdditionalSlides: 0,
        watchSlidesProgress: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
      });

      const updateDots = () => {
        if (!showDots) return;
        const active = swiper.realIndex % slideCount;
        dots.forEach((d, i) => d.classList.toggle('is-active', i === active));
      };
      updateDots();
      swiper.on('slideChange', updateDots);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          swiper.update();
          wrap.classList.add('is-ready');
        });
      });
    };

    setup();

    /* ---------- ブレークポイント変更時の再構築 ---------- */
    let resizeTimer;
    const onBP = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        destroy();
        setup();
      }, CONFIG.DEBOUNCE);
    };

    if (bp.addEventListener) {
      bp.addEventListener('change', onBP);
    } else {
      bp.addListener(onBP);
    }

    /* ---------- Shopifyテーマエディタの再ロード対応 ---------- */
    const onSectionEvent = (e) => {
      if (e.target?.querySelector?.(`[data-section-id="${sid}"]`)) {
        destroy();
        setup();
      }
    };
    document.addEventListener('shopify:section:load', onSectionEvent);
    document.addEventListener('shopify:section:select', onSectionEvent);
  };

  const boot = () => {
    document.querySelectorAll('[data-sbs-wrap]').forEach(initWrap);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();